import React, { useEffect, useRef, useState } from 'react';

import { buttons, colors } from '@pray/shared/components/foundations';
import Button from '@pray/shared/components/ui/Button';
import Image from '@pray/shared/components/ui/Image/Image';
import { cn } from '@pray/shared/utils/styles';

import useAppRoutes from 'hooks/useAppRoutes';

import type { StepProps, WorkflowSectionProps } from '../types';

const WorkflowStep: React.FC<StepProps> = ({ tag, title, description, buttonText, imageSrc, isVisible }) => {
  const appRoutes = useAppRoutes();
  return (
    <div className="items-left flex flex-col gap-12 py-10 md:flex-row md:py-16 lg:py-20">
      <div className="flex-1">
        {tag && (
          <span
            className={cn(
              'mb-2 inline-block opacity-0 text-xs font-[650] text-[#E9BF6E] md:mb-4 md:text-sm',
              isVisible && 'animate-slide-in-left opacity-1'
            )}
          >
            {tag}
          </span>
        )}
        <div
          className={cn(
            'flex flex-col gap-2 opacity-0 md:gap-3 lg:gap-6',
            isVisible && 'animate-slide-in-left opacity-1'
          )}
        >
          <h3 className="text-2xl font-bold md:text-[28px] lg:text-[40px] lg:leading-[56px]">{title}</h3>
          <p className="font-[450] text-text-secondary md:text-lg">{description}</p>
        </div>
        <Button
          data-tapeventname={`Workflow CTA - ${tag || title}`}
          variant={buttons.variant.secondary}
          color={colors.pray_featured}
          className={cn('mt-5 opacity-0 md:mt-8', isVisible && 'opacity-1 animate-slide-in-left')}
          to={appRoutes.getStarted()}
        >
          {buttonText}
        </Button>
      </div>
      <div
        className={cn(
          'grow md:max-w-96 lg:w-[696px] lg:max-w-[45vw] lg:flex-1 opacity-0',
          isVisible && 'animate-slide-in-right opacity-1'
        )}
      >
        <Image className="w-full justify-self-center rounded-lg " src={imageSrc} />
      </div>
    </div>
  );
};

const WorkflowSection: React.FC<WorkflowSectionProps> = ({ howItWorksSectionTitle, steps }) => {
  // State to track visibility of each step - first step starts visible
  const [visibility, setVisibility] = useState<{ [key: number]: boolean }>({ 0: true });
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Create refs for each step element, ensuring stable refs across renders
  const stepRefs = useRef<React.RefObject<HTMLDivElement>[]>([]);
  stepRefs.current = steps.map((_, i) => stepRefs.current[i] ?? React.createRef<HTMLDivElement>());

  useEffect(() => {
    const observerOptions = {
      rootMargin: '0px',
      threshold: 0.4, // Trigger when 40% of the element is visible
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        // Get index from data attribute
        const targetIndex = (entry.target as HTMLElement).dataset.observeIndex;
        if (entry.isIntersecting && targetIndex !== undefined) {
          const index = parseInt(targetIndex, 10);
          setVisibility((prev) => ({ ...prev, [index]: true }));
          // Unobserve after it becomes visible
          if (observerRef.current) {
            observerRef.current.unobserve(entry.target);
          }
        }
      });
    };

    // Create the observer instance only once
    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(observerCallback, observerOptions);
    }

    const observer = observerRef.current;

    // Observe each step ref
    stepRefs.current.forEach((ref, index) => {
      const element = ref.current;
      // Observe only if the element exists and hasn't become visible yet
      if (element && !visibility[index]) {
        element.dataset.observeIndex = index.toString(); // Add index data attribute
        observer.observe(element);
      }
    });

    // Cleanup function
    return () => {
      observer.disconnect();
    };
    // Rerun effect if steps array changes length
  }, [steps.length]); // Add visibility to dependencies to re-observe if needed

  return (
    <section className="overflow-x-hidden py-16 text-white">
      <div className="mx-auto max-w-7xl">
        <h2 className="text-left text-3xl font-bold md:text-[32px] lg:text-5xl lg:leading-[64px]">
          {howItWorksSectionTitle}
        </h2>
        {steps.map((step, index) => (
          <div key={step.id || `step-${index + 1}`} ref={stepRefs.current[index]}>
            <WorkflowStep {...step} isVisible={!!visibility[index]} />
          </div>
        ))}
      </div>
    </section>
  );
};

export default WorkflowSection;
